/**
 * Phone Input Component
 * Handles phone number formatting with country-specific masks
 * Single Responsibility: Phone input behavior and formatting
 * 
 * @package betbeast
 */

import { phoneCountryHint } from '../modules/phone-country-rules.js';

export class PhoneInput {
    constructor(phoneCodeSelect, phoneInput, countryData) {
        //console.log('PhoneInput: Constructor called', {
        //     phoneCodeSelect,
        //     phoneInput,
        //     countryData
        // });
        this.phoneCodeSelect = phoneCodeSelect;
        this.phoneInput = phoneInput;
        this.countryData = countryData || {};
        this.themeUrl = window.accountData?.themeUrl || '/app/themes/betbeast';
        this.flagsBaseUrl = `${this.themeUrl}/dist/images/flags-webp`;
        this.selectedOption = null;
        this.customDropdown = null;
        this.optionsData = [];
        this.optionsRendered = false;
        
        this.init();
    }

    init() {
        if (!this.phoneCodeSelect || !this.phoneInput) {
            console.error('PhoneInput: Missing required elements', {
                phoneCodeSelect: this.phoneCodeSelect,
                phoneInput: this.phoneInput
            });
            return;
        }
        
        //console.log('PhoneInput: Initializing custom phone code dropdown');
        
        this.createCustomDropdown();

        // One handler for every country: it reads the mask in force from dataset.mask, so
        // a country switch never leaves an earlier country's mask running.
        this.phoneInput.addEventListener('input', (e) => {
            const mask = this.phoneInput.dataset.mask;
            if (!mask) return;
            e.target.value = this.applyMask(this.removeMask(e.target.value), mask);
        });
    }

    /**
     * Create custom dropdown with flags
     */
    createCustomDropdown() {
        const phoneCodeWrapper = this.phoneCodeSelect.closest('.field__phone-code');
        if (!phoneCodeWrapper) return;

        // Get initial country code
        const countryHiddenInput = document.querySelector('input[type="hidden"][name="country"]');
        const countrySelect = document.querySelector('#modal_country');
        let initialCountryCode = '';
        
        if (countryHiddenInput && countryHiddenInput.value) {
            initialCountryCode = countryHiddenInput.value.split(':')[0];
        } else if (countrySelect && countrySelect.value) {
            initialCountryCode = countrySelect.value.split(':')[0];
        }

        // Build options data
        const optionsData = Array.from(this.phoneCodeSelect.options).map(option => {
            if (!option.value) return null;
            
            const countryAttr = option.getAttribute('data-country') || '';
            const countryCode = countryAttr.split(':')[0]; // Extract "CA" from "CA:CAD"
            
            if (!countryCode) {
                // If no data-country attribute, try to get country name from option text
                const optionText = option.textContent.trim();
                const phoneCode = option.value;
                const displayCode = phoneCode.startsWith('+') ? phoneCode : `+${phoneCode}`;
                
                return {
                    phoneCode,
                    displayCode,
                    name: optionText,
                    countryCode: '',
                    countryCodeLower: '',
                    mask: null
                };
            }
            
            const phoneCode = option.value;
            const displayCode = phoneCode.startsWith('+') ? phoneCode : `+${phoneCode}`;
            const countryData = this.countryData[countryCode] || {};
            
            return {
                phoneCode,
                displayCode,
                name: countryData.name || countryCode,
                countryCode,
                countryCodeLower: countryCode.toLowerCase(),
                mask: countryData.mask || null
            };
        }).filter(opt => opt !== null);

        // Find initially selected option. The server marks the player's own country
        // selected; read that element's country rather than looking the value up, which
        // finds the first option sharing the dial code (+44 Guernsey before the UK).
        const selectedEl = this.phoneCodeSelect.options[this.phoneCodeSelect.selectedIndex];
        const selectedCountry = selectedEl ? (selectedEl.getAttribute('data-country') || '').split(':')[0] : '';
        let selectedOption = initialCountryCode
            ? optionsData.find(opt => opt.countryCode === initialCountryCode)
            : (selectedCountry && optionsData.find(opt => opt.countryCode === selectedCountry))
                || optionsData.find(opt => this.phoneCodeSelect.querySelector(`[value="${opt.phoneCode}"]`)?.selected);

        if (!selectedOption) selectedOption = optionsData[0];
        this.selectedOption = selectedOption;

        // Hide original select
        this.phoneCodeSelect.style.display = 'none';
        this.phoneCodeSelect.value = selectedOption.phoneCode;
        this.selectOwnOption(selectedOption);

        const triggerFlagImg = this.getFlagImgMarkup(
            selectedOption.countryCodeLower,
            selectedOption.name,
            { lazy: false, decorative: false }
        );

        // Create custom dropdown HTML
        this.customDropdown = document.createElement('div');
        this.customDropdown.className = 'phone-code-dropdown';
        this.customDropdown.innerHTML = `
            <button type="button" class="phone-code-dropdown__trigger" aria-haspopup="listbox" aria-expanded="false">
                ${triggerFlagImg}
                <span class="phone-code-dropdown__code">${selectedOption.displayCode}</span>
                <i class="icon fa fa-angle-down fa-fw" aria-hidden="true"></i>
            </button>
            <div class="phone-code-dropdown__menu" role="listbox" aria-hidden="true">
                <div class="phone-code-dropdown__search-wrapper">
                    <input type="text" class="phone-code-dropdown__search" placeholder="Search country..." autocomplete="off" />
                </div>
                <ul class="phone-code-dropdown__list"></ul>
            </div>
        `;

        phoneCodeWrapper.appendChild(this.customDropdown);
        this.updateHint(selectedOption);

        // Store references
        this.trigger = this.customDropdown.querySelector('.phone-code-dropdown__trigger');
        this.menu = this.customDropdown.querySelector('.phone-code-dropdown__menu');
        this.searchInput = this.customDropdown.querySelector('.phone-code-dropdown__search');
        this.list = this.customDropdown.querySelector('.phone-code-dropdown__list');
        this.triggerFlag = this.customDropdown.querySelector('.phone-code-dropdown__flag');
        this.triggerCode = this.customDropdown.querySelector('.phone-code-dropdown__code');

        // Keep the option list empty until the dropdown opens so flag SVGs are not requested on page load.
        this.optionsData = optionsData;
        
        // Apply initial mask
        if (selectedOption.mask) {
            this.applyMaskToPhone(selectedOption.mask);
        }
        
        // Attach event listeners after everything is set up
        //console.log('PhoneInput: About to attach event listeners', {
        //     trigger: this.trigger,
        //     menu: this.menu,
        //     searchInput: this.searchInput
        // });
        this.attachEventListeners();
    }

    /**
     * Render dropdown options
     * @param {string} filter - Search filter text
     */
    renderOptions(filter = '') {
        if (!this.list) return;

        const filterLower = filter.toLowerCase();
        const filteredOptions = filter 
            ? this.optionsData.filter(opt => 
                opt.name.toLowerCase().includes(filterLower) || 
                opt.displayCode.includes(filterLower))
            : this.optionsData;

        this.list.innerHTML = filteredOptions.map((opt, idx) => {
            const flagImg = this.getFlagImgMarkup(
                opt.countryCodeLower,
                opt.name,
                { lazy: true, decorative: true }
            );
            
            return `
                <li class="phone-code-dropdown__option" data-phone-code="${opt.phoneCode}" data-country-code="${opt.countryCode}" data-value="${opt.phoneCode}" role="option">
                    ${flagImg}
                    <span class="phone-code-dropdown__name">${opt.name}</span>
                    <span class="phone-code-dropdown__code">${opt.displayCode}</span>
                </li>
            `;
        }).join('');
    }

    /**
     * Build a flag image URL
     * @param {string} countryCodeLower - Country code in lowercase
     * @returns {string}
     */
    getFlagImageUrl(countryCodeLower) {
        return `${this.flagsBaseUrl}/${countryCodeLower}.webp`;
    }

    /**
     * Get image markup for a flag
     * @param {string} countryCodeLower - Country code in lowercase
     * @param {string} label - Accessible label
     * @param {Object} options - Rendering options
     * @param {boolean} options.lazy - Whether to lazy-load the image
     * @param {boolean} options.decorative - Whether the flag is purely decorative
     * @returns {string}
     */
    getFlagImgMarkup(countryCodeLower, label, { lazy = false, decorative = false } = {}) {
        if (!countryCodeLower) {
            return '';
        }

        const src = this.getFlagImageUrl(countryCodeLower);
        const altAttrs = decorative
            ? 'alt="" aria-hidden="true"'
            : `alt="${label}"`;
        const loadingAttr = lazy ? 'loading="lazy"' : 'loading="eager"';

        return `
            <img class="phone-code-dropdown__flag" src="${src}" width="24" height="18" ${loadingAttr} decoding="async" ${altAttrs} />
        `;
    }

    /**
     * Update an existing <img> element to point at the correct flag
     * @param {HTMLImageElement|null} imgEl - The <img> element to update
     * @param {string} countryCodeLower - Country code in lowercase
     * @param {string} label - Accessible label
     * @returns {boolean}
     */
    updateFlagImage(imgEl, countryCodeLower, label) {
        if (!imgEl || !countryCodeLower) {
            return false;
        }

        imgEl.src = this.getFlagImageUrl(countryCodeLower);
        imgEl.alt = label;
        imgEl.removeAttribute('aria-hidden');
        return true;
    }

    /**
     * Create an image flag element for the trigger button.
     * @param {string} label - Accessible label
     * @returns {HTMLImageElement}
     */
    createTriggerFlagElement(label) {
        const img = document.createElement('img');
        img.setAttribute('class', 'phone-code-dropdown__flag');
        img.setAttribute('alt', label);
        img.setAttribute('width', '24');
        img.setAttribute('height', '18');
        img.setAttribute('loading', 'eager');
        img.setAttribute('decoding', 'async');
        return img;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
        //console.log('PhoneInput: attachEventListeners called', {
        //     trigger: this.trigger,
        //     menu: this.menu
        // });
        // Toggle dropdown
        this.trigger.addEventListener('click', (e) => {
            //console.log('PhoneInput: Trigger clicked');
            e.preventDefault();
            e.stopPropagation();
            
            const isOpen = this.trigger.getAttribute('aria-expanded') === 'true';
            if (isOpen) {
                this.closeDropdown();
            } else {
                this.openDropdown();
            }
        });

        // Search input
        this.searchInput.addEventListener('input', (e) => {
            this.renderOptions(e.target.value);
        });

        // Option clicks
        this.list.addEventListener('click', (e) => {
            const optionEl = e.target.closest('.phone-code-dropdown__option');
            if (optionEl) {
                // Several countries share a dial code (+39 Italy and Vatican, +44, +1):
                // match the country picked, not the first option with that code.
                const phoneCode = optionEl.dataset.phoneCode;
                const countryCode = optionEl.dataset.countryCode || '';
                const option = (countryCode && this.optionsData.find(opt => opt.countryCode === countryCode))
                    || this.optionsData.find(opt => opt.phoneCode === phoneCode);
                if (option) {
                    this.selectOption(option, false); // false = don't sync country when user manually selects
                    this.closeDropdown();
                }
            }
        });

        // Close on outside click
        document.addEventListener('click', (e) => {
            if (!this.customDropdown.contains(e.target)) {
                this.closeDropdown();
            }
        });

        // Keyboard navigation
        this.searchInput.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.closeDropdown();
            }
        });
        
        // Sync from country select to phone code
        this.setupCountrySync();
    }

    /**
     * Setup country select synchronization
     */
    setupCountrySync() {
        const countrySelect = document.querySelector('#modal_country');
        const countryHiddenInput = document.querySelector('input[type="hidden"][name="country"]');
        
        if (countrySelect) {
            countrySelect.addEventListener('change', (e) => {
                const countryValue = e.target.value;
                const countryCode = countryValue.split(':')[0]; // Extract "CA" from "CA:CAD"
                const option = this.optionsData.find(opt => opt.countryCode === countryCode);
                
                if (option) {
                    this.selectOption(option, true); // true = this is from country change, update phone code
                }
            });
        }
    }
    
    /**
     * Open dropdown
     */
    openDropdown() {
        //console.log('PhoneInput: Opening dropdown', {
        //     menu: this.menu,
        //     currentDisplay: this.menu.style.display,
        //     currentAriaHidden: this.menu.getAttribute('aria-hidden')
        // });

        if (!this.optionsRendered) {
            this.renderOptions();
            this.optionsRendered = true;
        }

        this.trigger.setAttribute('aria-expanded', 'true');
        this.menu.setAttribute('aria-hidden', 'false');
        this.menu.classList.add('is-open');
        //console.log('PhoneInput: After adding is-open class', {
        //     classList: this.menu.classList,
        //     computedDisplay: window.getComputedStyle(this.menu).display
        // });
        setTimeout(() => this.searchInput.focus(), 50);
    }

    /**
     * Close dropdown
     */
    closeDropdown() {
        //console.log('PhoneInput: Closing dropdown');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.menu.setAttribute('aria-hidden', 'true');
        this.menu.classList.remove('is-open');
        this.searchInput.value = '';
        this.list.innerHTML = '';
        this.optionsRendered = false;
    }

    /**
     * Select an option
     * @param {Object} option - Option data
     * @param {boolean} fromCountryChange - Internal flag (not used but kept for compatibility)
     */
    selectOption(option, fromCountryChange = false) {
        this.selectedOption = option;
        
        // Update trigger
        if (!this.triggerFlag && option.countryCodeLower) {
            const triggerFlag = this.createTriggerFlagElement(option.name);
            this.trigger.insertBefore(triggerFlag, this.triggerCode);
            this.triggerFlag = triggerFlag;
        }

        const flagUpdated = this.updateFlagImage(this.triggerFlag, option.countryCodeLower, option.name);
        if (this.triggerFlag) {
            if (flagUpdated) {
                this.triggerFlag.style.display = '';
            } else {
                this.triggerFlag.style.display = 'none';
            }
        }
        this.triggerCode.textContent = option.displayCode;
        
        // Update hidden select
        this.phoneCodeSelect.value = option.phoneCode;
        this.selectOwnOption(option);
        this.phoneCodeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        
        // Apply mask
        if (option.mask) {
            this.applyMaskToPhone(option.mask);
        } else {
            // Remove mask
            const unmasked = this.removeMask(this.phoneInput.value);
            this.phoneInput.value = unmasked;
            this.phoneInput.dataset.mask = '';
        }

        this.updateHint(option);
    }

    /**
     * Select the hidden select's option of this country. Setting .value alone lands
     * on the first option sharing the dial code (+39 Vatican before Italy).
     * @param {Object} option - Option data
     */
    selectOwnOption(option) {
        if (!option || !option.countryCode) return;
        const own = Array.from(this.phoneCodeSelect.options).find(
            el => (el.getAttribute('data-country') || '').split(':')[0] === option.countryCode
        );
        if (own) own.selected = true;
    }

    /**
     * Show the example number for the selected dial code in the field's floating
     * label, after its name: "Mobile Number · e.g. 506-234-5678". The label stays
     * up while the player types, the placeholder does not; the dial code is left
     * out because the list beside the field holds it. Only while country phone
     * rules are on: off, the label keeps the placeholder text it was built from.
     * @param {Object} option - Selected option
     */
    updateHint(option) {
        const countryRules = typeof accountData !== 'undefined'
            && accountData.validationRules
            && accountData.validationRules.phone
            ? accountData.validationRules.phone.country_rules
            : null;
        if (!countryRules) return;

        // FloatingLabel builds the label from the placeholder when the page loads,
        // before the modal opens and this component starts.
        const field = this.phoneInput.closest('.field');
        const label = field ? field.querySelector('.field__floating-label') : null;
        if (!label) return;

        const name = this.phoneInput.getAttribute('placeholder') || '';
        const hint = phoneCountryHint(option.phoneCode, countryRules, option.countryCode || undefined, true);
        label.textContent = hint ? name + ' · ' + hint : name;
    }

    /**
     * Apply mask to phone input; the input handler set up in init() keeps it applied.
     * @param {string} mask - Phone mask pattern
     */
    applyMaskToPhone(mask) {
        const currentUnmasked = this.removeMask(this.phoneInput.value);
        this.phoneInput.value = this.applyMask(currentUnmasked, mask);
        this.phoneInput.dataset.mask = mask;
    }

    /**
     * Apply phone number mask to value
     * @param {string} value - Raw phone number
     * @param {string} mask - Mask pattern (e.g., "(___) ___-____")
     * @returns {string} Formatted phone number
     */
    applyMask(value, mask) {
        return applyMask(value, mask);
    }

    /**
     * Remove phone number formatting
     * @param {string} value - Formatted phone number
     * @returns {string} Raw digits only
     */
    removeMask(value) {
        return removeMask(value);
    }
}

/**
 * Apply phone number mask to value. Also used by the cashier's phone field
 * (schema-phone-field.js), so a number is laid out the same in both.
 * @param {string} value - Raw phone number
 * @param {string} mask - Mask pattern (e.g., "(___) ___-____")
 * @returns {string} Formatted phone number
 */
export function applyMask(value, mask) {
    if (!mask) return value;

    const digits = value.replace(/\D/g, '');
    let result = '';
    let digitIndex = 0;

    // Apply mask pattern
    for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
        if (mask[i] === '_') {
            result += digits[digitIndex++];
        } else {
            result += mask[i];
        }
    }

    // Allow additional digits beyond the mask (up to max validation limit)
    if (digitIndex < digits.length) {
        result += ' ' + digits.substring(digitIndex);
    }

    return result;
}

/**
 * Remove phone number formatting
 * @param {string} value - Formatted phone number
 * @returns {string} Raw digits only
 */
export function removeMask(value) {
    return value.replace(/\D/g, '');
}
